/**
 * The Report an Issue form (opened through ReportIssueProvider): a panel docked in the
 * bottom-right corner, just above the life ring, not a pop-up. It is non-modal — the page
 * behind stays usable, so the reporter can open what they want to show and then capture
 * it — and clicking the page does not close it. Escape or × closes it; the report is kept.
 *
 * Title, what happened, category, priority and an optional screenshot that can be marked
 * up before sending. The page, browser and recent errors are recorded automatically — the
 * form says so — and the screenshot is attached only if the reporter adds one.
 *
 * One press, one Issue: the form holds a `clientSubmissionId` from the moment a new report
 * starts. A failed send keeps the form open with the same id, so sending again returns
 * the Issue the first press made instead of creating a second.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAppInfo } from "@/hooks/use-app-info";
import { useCreateSupportIssue } from "@/hooks/use-support-issues";
import { type IssueDiagnosticsSnapshot, snapshotIssueDiagnostics } from "@/lib/issue-diagnostics";
import type { Mark } from "@/lib/markup/marks";
import {
  createDraft,
  disposeDraft,
  fileExtension,
  prepareScreenshotParts,
  type ScreenshotDraft,
  withMarks,
} from "@/lib/markup/screenshot-draft";
import {
  CAPTURE_IGNORE_PROPS,
  captureScreen,
  nextPaint,
  settleAnimations,
} from "@/lib/screenshot-capture";
import {
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  SUPPORT_ISSUE_CATEGORIES,
  SUPPORT_ISSUE_PRIORITIES,
} from "@/lib/support-issue-labels";
import { MarkupEditor } from "./MarkupEditor";
import { ScreenshotField, UPLOAD_MAX_BYTES } from "./ScreenshotField";

const NO_CATEGORY = "none";

const formSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Give the issue a title")
    .max(200, "Keep the title under 200 characters"),
  description: z
    .string()
    .trim()
    .min(1, "Say what happened")
    .max(5000, "Keep it under 5,000 characters"),
  category: z.enum([NO_CATEGORY, ...SUPPORT_ISSUE_CATEGORIES]),
  priority: z.enum(SUPPORT_ISSUE_PRIORITIES),
});

type FormValues = z.infer<typeof formSchema>;

const EMPTY: FormValues = { title: "", description: "", category: NO_CATEGORY, priority: "normal" };

/** Which surface is on screen: the corner panel, nothing (mid-capture), or the markup layer. */
type Phase = "form" | "capturing" | "markup";

interface ReportIssuePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Told whether a report has been started (typed into, or a screenshot added). */
  onInProgressChange: (inProgress: boolean) => void;
}

export function ReportIssuePanel({
  open,
  onOpenChange,
  onInProgressChange,
}: ReportIssuePanelProps) {
  const navigate = useNavigate();
  const { data: appInfo } = useAppInfo();
  const create = useCreateSupportIssue();
  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: EMPTY });

  const [phase, setPhase] = useState<Phase>("form");
  const [draft, setDraft] = useState<ScreenshotDraft | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [busyImage, setBusyImage] = useState(false);
  /** Set when a new report starts; kept across failed sends (one press, one Issue). */
  const submissionId = useRef<string | null>(null);
  /** What was happening when the reporter opened the form — not what the form itself did. */
  const diagnostics = useRef<IssueDiagnosticsSnapshot | null>(null);

  useEffect(() => {
    if (!open || submissionId.current !== null) return;
    submissionId.current = crypto.randomUUID();
    diagnostics.current = snapshotIssueDiagnostics();
  }, [open]);

  const { isDirty } = form.formState;
  const inProgress = isDirty || draft !== null;
  useEffect(() => onInProgressChange(inProgress), [inProgress, onInProgressChange]);

  const reset = () => {
    disposeDraft(draft);
    setDraft(null);
    form.reset(EMPTY);
    submissionId.current = null;
    diagnostics.current = null;
    setPhase("form");
  };

  const replaceDraft = (next: ScreenshotDraft | null) => {
    if (draft && draft !== next) disposeDraft(draft);
    setDraft(next);
  };

  const capture = async () => {
    setBusyImage(true);
    setPhase("capturing");
    // Toasts are left out of the capture, so this can say what's happening.
    const progress = toast.loading("Capturing the screen…");
    try {
      // Let the form leave the screen so the capture shows what's behind it.
      await nextPaint();
      await settleAnimations();
      const shot = await captureScreen();
      replaceDraft(await createDraft(shot.blob, "capture"));
      setPhase("markup");
    } catch (err) {
      setPhase("form");
      toast.error(
        `The screen couldn't be captured${err instanceof Error ? ` (${err.message})` : ""}. Try Upload an image instead.`,
      );
    } finally {
      toast.dismiss(progress);
      setBusyImage(false);
    }
  };

  const upload = async (file: File) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Choose a PNG, JPEG or WebP image.");
      return;
    }
    if (file.size > UPLOAD_MAX_BYTES) {
      toast.error("That image is over 25 MB. Choose a smaller one.");
      return;
    }
    setBusyImage(true);
    try {
      replaceDraft(await createDraft(file, "upload"));
      setPhase("markup");
    } catch {
      toast.error("That image couldn't be read. Try another one.");
    } finally {
      setBusyImage(false);
    }
  };

  const finishMarkup = async (marks: Mark[]) => {
    if (!draft) {
      setPhase("form");
      return;
    }
    setBusyImage(true);
    try {
      setDraft(await withMarks(draft, marks));
    } catch {
      toast.error("The marks couldn't be drawn onto the screenshot.");
    } finally {
      setBusyImage(false);
      setPhase("form");
    }
  };

  const submit = form.handleSubmit(async (values) => {
    const clientSubmissionId = submissionId.current ?? crypto.randomUUID();
    submissionId.current = clientSubmissionId;
    const snap = diagnostics.current ?? snapshotIssueDiagnostics();

    const body = new FormData();
    setPreparing(true);
    try {
      const parts = draft ? await prepareScreenshotParts(draft) : null;
      body.set(
        "data",
        JSON.stringify({
          clientSubmissionId,
          title: values.title,
          description: values.description,
          category: values.category === NO_CATEGORY ? null : values.category,
          priority: values.priority,
          context: { ...snap.context, appVersion: appInfo?.version ?? undefined },
          consoleTrail: snap.consoleTrail,
          networkTrail: snap.networkTrail,
          screenshot: {
            markedUp: parts?.markedUp ?? false,
            redacted: parts?.redacted ?? false,
            notes: parts?.notes ?? [],
          },
        }),
      );
      if (parts) {
        body.set("screenshot", parts.screenshot, `screenshot.${fileExtension(parts.screenshot)}`);
        if (parts.original) {
          body.set("original", parts.original, `original.${fileExtension(parts.original)}`);
        }
        body.set(
          "delyverImage",
          parts.delyverImage,
          `delyver.${fileExtension(parts.delyverImage)}`,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "The screenshot couldn't be prepared.");
      setPreparing(false);
      return;
    }
    setPreparing(false);

    create.mutate(body, {
      onSuccess: ({ issue }) => {
        toast.success(`Thanks — ${issue.code} is with the team.`, {
          action: {
            label: "View",
            onClick: () => navigate({ to: "/dashboard/issues/$id", params: { id: issue.id } }),
          },
        });
        reset();
        onOpenChange(false);
      },
      onError: (err: Error) =>
        toast.error(
          // A thrown TypeError means the request never reached Staff (offline, server down)
          // — the browser's own "Failed to fetch" says nothing useful.
          err instanceof TypeError
            ? "Couldn't reach Staff, so your issue wasn't sent. Your report is still here — press Send again."
            : `${err.message || "Your issue wasn't sent."} Press Send again to retry.`,
        ),
    });
  });

  const sending = preparing || create.isPending;

  return (
    <>
      <DialogPrimitive.Root
        open={open && phase === "form"}
        onOpenChange={onOpenChange}
        modal={false}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Content
            {...CAPTURE_IGNORE_PROPS}
            data-slot="dialog-content"
            // Clicking the page (or a Select's list, which renders outside the panel) must
            // not close a half-written report.
            onInteractOutside={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              form.setFocus("title");
            }}
            className="fixed inset-x-2 bottom-9 z-40 flex max-h-[80dvh] flex-col rounded-lg border border-border bg-card text-card-foreground shadow-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2 sm:inset-x-auto sm:right-3 sm:max-h-[calc(100dvh-5rem)] sm:w-[400px]"
          >
            <div className="flex items-start gap-2 border-b border-border py-3 pr-2 pl-4">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <DialogPrimitive.Title className="font-display text-base font-semibold text-heading">
                  Report an issue
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-xs text-muted-foreground">
                  We include the page, your browser and recent errors. A screenshot only if you add
                  one.
                </DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close — your report is kept"
                  title="Close — your report is kept"
                >
                  <XIcon className="size-4" />
                </Button>
              </DialogPrimitive.Close>
            </div>

            <Form {...form}>
              <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Plan totals don't add up"
                            maxLength={200}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What happened?</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={4}
                            maxLength={5000}
                            placeholder="What did you do, what did you expect, and what happened instead?"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={NO_CATEGORY}>Not sure</SelectItem>
                              {SUPPORT_ISSUE_CATEGORIES.map((c) => (
                                <SelectItem key={c} value={c}>
                                  {CATEGORY_LABELS[c]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Priority</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {SUPPORT_ISSUE_PRIORITIES.map((p) => (
                                <SelectItem key={p} value={p}>
                                  {PRIORITY_LABELS[p]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>

                  <ScreenshotField
                    draft={draft}
                    busy={busyImage || sending}
                    onCapture={capture}
                    onUpload={upload}
                    onMarkUp={() => setPhase("markup")}
                    onRemove={() => replaceDraft(null)}
                  />
                </div>
                <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={sending}
                    onClick={() => {
                      reset();
                      onOpenChange(false);
                    }}
                  >
                    Discard
                  </Button>
                  <Button type="submit" disabled={sending || busyImage}>
                    {preparing ? "Preparing…" : create.isPending ? "Sending…" : "Send issue"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {draft && (
        <MarkupEditor
          open={open && phase === "markup"}
          origin={draft.origin}
          imageUrl={draft.sourceUrl}
          width={draft.width}
          height={draft.height}
          initialMarks={draft.marks}
          onDone={finishMarkup}
          onCancel={() => setPhase("form")}
        />
      )}
    </>
  );
}
